import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { siteData } from '../data';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const location = useLocation();
  const timeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setIsMobileDropdownOpen(false);
  }, [location]);

  // Prevent scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleMouseEnter = (hasDropdown) => {
    if (!hasDropdown) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsDropdownOpen(true);
  };

  const handleMouseLeave = (hasDropdown) => {
    if (!hasDropdown) return;
    timeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 300); // 300ms delay
  };

  const navLinks = [
    { name: 'Forside', href: '/' },
    { 
      name: 'Behandlinger', 
      href: '/#behandlinger',
      dropdown: [
        { name: 'Ansigtsbehandling', href: '/behandlinger/ansigtsbehandlinger' },
        { name: 'Massage', href: '/behandlinger/massage' }
      ]
    },
    { name: 'Priser', href: '/priser' },
    { name: 'Kontakt', href: '/kontakt' },
  ];

  const isInternalLink = (href) => href.startsWith('/') && !href.includes('#');

  // Use the color logo for the navbar and sidebar
  const logoUrl = "https://assets.auxodev.dk/assets/827bf07b-63d5-4b3e-a53e-0e890a6a1d87/69f2413c-fcae-456a-8125-774bce14700a.png";

  return (
    <nav className="fixed w-full z-50 bg-white shadow-md py-4 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center">
          {/* Logo & Mobile Toggle Row */}
          <div className="w-full flex justify-between md:justify-center items-center relative">
            {/* Mobile spacer to balance the toggle button */}
            <div className="md:hidden w-10"></div>
            
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center">
                <img 
                  src={logoUrl} 
                  alt="Pai Thai Massage Logo" 
                  className="h-20 md:h-24 w-auto"
                />
              </Link>
            </div>

            <div className="md:hidden">
              <button
                onClick={() => setIsOpen(true)}
                className="inline-flex items-center justify-center p-2 rounded-md text-stone-900 hover:text-[#b0a697] transition-colors"
                aria-label="Åbn menu"
              >
                <Menu className="h-7 w-7" />
              </button>
            </div>
          </div>
          
          {/* Desktop Menu Links - Below Logo */}
          <div className="hidden md:block mt-6">
            <div className="flex items-center space-x-10">
              {navLinks.map((link) => (
                <div 
                  key={link.name} 
                  className="relative group"
                  onMouseEnter={() => handleMouseEnter(link.dropdown)}
                  onMouseLeave={() => handleMouseLeave(link.dropdown)}
                >
                  {link.dropdown ? (
                    <div className="flex items-center cursor-pointer text-sm font-medium transition-colors hover:text-[#b0a697] text-stone-700 uppercase tracking-wider">
                      <span>{link.name}</span>
                      <ChevronDown className={`ml-1 w-4 h-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </div>
                  ) : (
                    isInternalLink(link.href) ? (
                      <Link
                        to={link.href}
                        className={`text-sm transition-colors hover:text-[#b0a697] uppercase tracking-wider ${
                          link.name === 'Forside' ? 'text-black font-semibold' : 'font-medium ' + (location.pathname === link.href ? 'text-[#b0a697]' : 'text-stone-700')
                        }`}
                      >
                        {link.name}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        className="text-sm font-medium transition-colors hover:text-[#b0a697] text-stone-700 uppercase tracking-wider"
                      >
                        {link.name}
                      </a>
                    )
                  )}

                  {link.dropdown && isDropdownOpen && (
                    <div className="absolute left-0 mt-2 w-56 bg-white rounded-xl shadow-xl py-2 z-50 border border-stone-100 overflow-hidden">
                      {link.dropdown.map((subItem) => (
                        isInternalLink(subItem.href) ? (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            className="block px-6 py-3 text-xs font-medium text-stone-600 hover:text-[#b0a697] hover:bg-stone-50 uppercase tracking-widest transition-colors"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            {subItem.name}
                          </Link>
                        ) : (
                          <a
                            key={subItem.name}
                            href={subItem.href}
                            className="block px-6 py-3 text-xs font-medium text-stone-600 hover:text-[#b0a697] hover:bg-stone-50 uppercase tracking-widest transition-colors"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            {subItem.name}
                          </a>
                        )
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <a
                href={`tel:${siteData.phone}`}
                className="inline-flex items-center px-5 py-2 border border-transparent text-sm font-medium rounded-full shadow-sm text-white bg-[#b0a697] hover:bg-[#b0a697]/90 transition-colors"
              >
                <Phone className="w-4 h-4 mr-2" />
                {siteData.phone}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Menu */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm z-[60] md:hidden"
            />

            {/* Sidebar */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 bottom-0 w-[80%] max-w-sm bg-white z-[70] shadow-2xl md:hidden overflow-y-auto"
            >
              <div className="flex flex-col h-full">
                {/* Sidebar Header */}
                <div className="flex items-center justify-between p-6 border-b border-stone-100">
                  <Link to="/" onClick={() => setIsOpen(false)}>
                    <img 
                      src={logoUrl} 
                      alt="Pai Thai Massage Logo" 
                      className="h-16 w-auto"
                    />
                  </Link>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-2 rounded-full hover:bg-stone-100 text-stone-500 transition-colors"
                    aria-label="Luk menu"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                {/* Sidebar Links */}
                <div className="flex-1 px-4 py-8 space-y-2">
                  {navLinks.map((link) => (
                    <div key={link.name} className="py-1">
                      {link.dropdown ? (
                        <div className="space-y-1">
                          <button
                            onClick={() => setIsMobileDropdownOpen(!isMobileDropdownOpen)}
                            className="w-full flex justify-between items-center py-3 px-4 rounded-full text-lg font-medium text-stone-700 hover:text-[#b0a697] hover:bg-stone-50 transition-colors uppercase tracking-wider"
                          >
                            <span>{link.name}</span>
                            <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isMobileDropdownOpen ? 'rotate-180' : ''}`} />
                          </button>
                          <AnimatePresence>
                            {isMobileDropdownOpen && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden bg-stone-50 rounded-2xl mx-2"
                              >
                                {link.dropdown.map((subItem) => (
                                  isInternalLink(subItem.href) ? (
                                    <Link
                                      key={subItem.name}
                                      to={subItem.href}
                                      className="block px-6 py-3 text-sm font-medium text-stone-600 hover:text-[#b0a697] hover:bg-stone-100 rounded-full mx-1 my-1 uppercase tracking-wider transition-colors"
                                      onClick={() => setIsOpen(false)}
                                    >
                                      {subItem.name}
                                    </Link>
                                  ) : (
                                    <a
                                      key={subItem.name}
                                      href={subItem.href}
                                      className="block px-6 py-3 text-sm font-medium text-stone-600 hover:text-[#b0a697] hover:bg-stone-100 rounded-full mx-1 my-1 uppercase tracking-wider transition-colors"
                                      onClick={() => setIsOpen(false)}
                                    >
                                      {subItem.name}
                                    </a>
                                  )
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ) : (
                        isInternalLink(link.href) ? (
                          <Link
                            to={link.href}
                            className={`block py-3 px-4 rounded-full text-lg uppercase tracking-wider transition-colors hover:bg-stone-50 ${
                              link.name === 'Forside' ? 'text-black font-semibold' : 'font-medium ' + (location.pathname === link.href ? 'text-[#b0a697]' : 'text-stone-700 hover:text-[#b0a697]')
                            }`}
                            onClick={() => setIsOpen(false)}
                          >
                            {link.name}
                          </Link>
                        ) : (
                          <a
                            href={link.href}
                            className="block py-3 px-4 rounded-full text-lg font-medium text-stone-700 hover:text-[#b0a697] hover:bg-stone-50 uppercase tracking-wider transition-colors"
                            onClick={() => setIsOpen(false)}
                          >
                            {link.name}
                          </a>
                        )
                      )}
                    </div>
                  ))}
                </div>

                {/* Sidebar Footer */}
                <div className="p-6 border-t border-stone-100 bg-stone-50">
                  <a
                    href={`tel:${siteData.phone}`}
                    className="flex items-center justify-center w-full py-4 px-6 bg-[#b0a697] text-white rounded-full font-semibold shadow-lg shadow-[#b0a697]/20 hover:bg-[#b0a697]/90 transition-all active:scale-[0.98]"
                  >
                    <Phone className="w-5 h-5 mr-3" />
                    Ring: {siteData.phone}
                  </a>
                  <p className="mt-4 text-center text-xs text-stone-400 uppercase tracking-widest">
                    Pai Thai Massage
                  </p>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
}
